<template>
  <div>
    <div :class="_.bar">
      <label :class="_.search">
        <x-icon name="search" :class="_.icon" />
        <form :class="_.form" action="." @submit.prevent.stop="handleSubmit">
          <input
            ref="input"
            v-model="localKeyword"
            type="search"
            :class="_.input"
            :placeholder="defaultKeyword || '请输入搜索关键词'"
            @focus="handleFocus"
          />
        </form>
        <x-icon
          v-if="localKeyword"
          name="close-circle"
          :class="_.clear"
          @click.native.prevent.stop="handleClearClick"
        />
      </label>
      <div
        v-if="queryKeywords && localKeyword && isFocus"
        :class="_.button"
        @click="handleCancel"
      >
        取消
      </div>
      <div v-else :class="_.button" @click="handleSubmit">搜索</div>
    </div>
    <div
      v-if="queryKeywords && isFocus && localKeyword"
      :class="_.candidate"
      :style="{
        top: `calc(${height}px - var(--page-header-height))`,
      }"
    >
      <div :class="[_.container, _._hairlineTop]">
        <x-waterfall
          :key="localKeyword"
          ref="list"
          manual
          disabled
          :class="_.list"
          :load="handleQueryKeywords"
        >
          <template #default="{ data }">
            <div
              :key="localKeyword"
              :class="[_.item, _._hairlineBottom]"
              @click="handleCandidateClick"
            >
              <x-icon name="search" :class="_.icon" />
              <div :class="[_.text, _._ellipsis]">
                <span>{{ localKeyword }}</span>
              </div>
            </div>
            <div
              v-for="item in data"
              :key="item"
              :class="[_.item, _._hairlineBottom]"
              @click="handleCandidateClick"
            >
              <x-icon name="search" :class="_.icon" />
              <div :class="[_.text, _._ellipsis]" v-html="item"></div>
            </div>
          </template>
        </x-waterfall>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'
  import { debounce, escape, escapeRegExp } from 'vtils'

  export default Vue.extend({
    model: {
      prop: 'keyword',
      event: 'change',
    },

    props: {
      keyword: {
        type: String,
        required: true,
      },
      defaultKeyword: {
        type: String,
        default: '',
      },
      queryKeywords: {
        type: Function,
        default: undefined,
      },
      updateOnClickKeyword: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      localKeyword: '',
      height: 0,
      isFocus: false,
    }),

    watch: {
      keyword: {
        immediate: true,
        handler() {
          this.localKeyword = this.keyword
        },
      },
      localKeyword: {
        immediate: true,
        handler: debounce(function () {
          if (this.queryKeywords && this.localKeyword && this.isFocus) {
            this.$refs.list.reload()
          }
        }, 600),
      },
      isFocus: {
        immediate: true,
        handler: debounce(function () {
          if (this.queryKeywords && this.localKeyword && this.isFocus) {
            this.$refs.list.reload()
          }
        }, 600),
      },
    },

    mounted() {
      const barNode = document.querySelector(`.${this._.bar}`)
      const rect = barNode.getBoundingClientRect()
      const height = rect.top + rect.height
      this.height = height
    },

    methods: {
      handleFocus() {
        this.isFocus = true
      },
      handleCancel() {
        this.localKeyword = ''
        this.isFocus = false
        this.$emit('change', '')
      },
      handleQueryKeywords({ pageNumber }) {
        return pageNumber === 1
          ? this.queryKeywords(this.localKeyword).then(list => {
              return list
                .map(item => {
                  let html = ''
                  if (item && typeof item === 'object') {
                    const text = item.text || item.title || ''
                    const keywords = item.keywords || []
                    if (text === this.localKeyword) {
                      html = ''
                    } else {
                      html = text
                      keywords.forEach(keyword => {
                        html = html.replace(
                          new RegExp(`(${escapeRegExp(keyword)})`, 'g'),
                          `[KEY]$1[/KEY]`,
                        )
                      })
                      html = escape(html)
                        .replace(/\[KEY\]/g, '<span>')
                        .replace(/\[\/KEY\]/g, '</span>')
                    }
                  } else {
                    html = escape(String(item))
                  }
                  return html
                })
                .filter(Boolean)
            })
          : []
      },
      handleSubmit() {
        this.$refs.input.blur()
        this.isFocus = false
        const keyword = this.localKeyword || this.defaultKeyword || ''
        this.$emit('change', keyword)
        this.$emit('confirm', keyword, '按钮搜索')
      },
      handleClearClick() {
        this.localKeyword = ''
        this.isFocus = false
        this.$emit('change', '')
      },
      handleCandidateClick(e) {
        const keyword = e.target.innerText.trim()
        this.isFocus = false
        if (this.updateOnClickKeyword) {
          this.$emit('change', keyword)
        }
        this.$emit('confirm', keyword, '联想搜索')
      },
    },
  })
</script>

<style lang="scss" module>
  .bar {
    display: flex;
    align-items: center;
    line-height: 1;

    .search {
      flex-grow: 1;
      display: flex;
      align-items: center;
      background: #f5f5f5;
      border-radius: 1000px;
      padding: 0 12px;
      height: 34px;

      .icon {
        flex-shrink: 0;
        font-size: 16px;
        color: #333;
      }

      .form {
        margin-left: 6px;
        flex-grow: 1;
        display: flex;
        align-items: center;

        .input {
          flex: 1;
          font-size: 14px;
          color: #333333;
          appearance: none;
          box-shadow: none;
          border: none;
          border-radius: 0;
          outline: none;
          background-color: transparent;

          /* clears the 'X' from Internet Explorer */
          &::-ms-clear,
          &::-ms-reveal {
            display: none;
            width: 0;
            height: 0;
          }

          /* clears the 'X' from Chrome */
          &::-webkit-search-decoration,
          &::-webkit-search-cancel-button,
          &::-webkit-search-results-button,
          &::-webkit-search-results-decoration {
            display: none;
          }

          &::placeholder {
            color: #888888;
          }
        }
      }

      .clear {
        flex-shrink: 0;
        font-size: 18px;
        color: #ccc;
      }
    }

    .button {
      margin-left: 12px;
      flex-shrink: 0;
      font-size: 16px;
      font-weight: bold;
      color: #222222;

      &:active {
        opacity: 0.9;
      }
    }
  }

  .candidate {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: white;
    padding-top: 12px;

    .container {
      height: 100%;

      .list {
        padding: 0 12px;
        height: 100%;
        overflow: auto;

        .item {
          padding: 12px 0;
          display: flex;
          align-items: center;

          .icon {
            flex-shrink: 0;
            font-size: 16px;
            color: #333;
          }

          .text {
            margin-left: 12px;
            flex-grow: 1;
            font-size: 14px;
            color: #333;

            span {
              color: #ff111c;
            }
          }

          &:active {
            opacity: 0.8;
          }
        }
      }
    }
  }
</style>
